import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  getAgileRules,
  getIssuesActionMenu,
  getIssuesContextMenu,
} from '../../../../packages/common/api';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DoneIcon from '../../../../packages/common/shared-ui/src/icons/DoneIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import TableSecondStepPopover from '../TableSecondStepPopover';

// Original class: contextPopover
const StyledContextPopover = styled(Popover)(theme => ({
  '& .MuiPaper-root': {
    overflow: 'hidden',
    borderRadius: '16px',
    border: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px rgba(33, 35, 70, 1)',
  },
}));
// Original class: scrollContainer
const StyledScrollContainer = styled(Box)(theme => ({
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#F1F2F8',
    marginRight: '2px',
    marginTop: '10px',
    marginBottom: '10px',
  },

  '&::-webkit-scrollbar': {
    width: '4px',
    backgroundColor: 'white !important',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },
}));
const ContextMenu = ({ open, id, position, closed, select, selectMenu }) => {
  const getLocalString = useTranslation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const [menuItems, setMenuItems] = useState([]);
  const [activeSubMenu, setActiveSubmenu] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const MenuItemsNames = {
    STATUS: 'status',
    EDIT: 'edit',
    COPY: 'copy',
    DELETE: 'delete',
    ASSIGNED: 'assigned',
    PRIORITY: 'priority',
  };
  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );
  useEffect(() => {
    open && setOpenMenu(open);
  }, [open]);
  useEffect(() => {
    position && setMenuPosition(position);
  }, [open, position]);
  useEffect(() => {
    if (menuData) {
      const items = [
        { name: MenuItemsNames.STATUS, subItems: menuData.statuses },
        { name: MenuItemsNames.ASSIGNED, subItems: menuData.assignedTo },
        { name: MenuItemsNames.PRIORITY, subItems: menuData.priorities },
      ];
      menuData.canEdit && items.unshift({ name: MenuItemsNames.EDIT });
      menuData.canCopy && items.push({ name: MenuItemsNames.COPY });
      menuData.canDelete && items.push({ name: MenuItemsNames.DELETE });
      setMenuItems(items);
    }
  }, [
    MenuItemsNames.PRIORITY,
    MenuItemsNames.ASSIGNED,
    MenuItemsNames.COPY,
    MenuItemsNames.DELETE,
    MenuItemsNames.EDIT,
    MenuItemsNames.STATUS,
    menuData,
  ]);
  useEffect(() => {
    let ignore = false;
    let params = {};
    id && (params.issues = id);

    async function getContextMenu() {
      setLoading(true);
      const response = await getIssuesContextMenu(params);
      if (!ignore) {
        return response;
      }
    }
    if (open && id) {
      getContextMenu()
        .then(res => {
          const { data: data } = res;
          setMenuData(data);
          setLoading(false);
        })
        .catch(error => handleError(error.response));
    }

    return () => {
      ignore = true;
    };
  }, [handleError, id, open]);

  const handleSubMenu = useCallback((event, name) => {
    setAnchorEl(event.currentTarget);
    setActiveSubmenu(name);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuItems([]);
    closed();
  }, [closed]);

  const handleSubMenuClose = useCallback(() => {
    setAnchorEl(null);
    setActiveSubmenu(null);
  }, []);
  const handleSubMenuSelect = useCallback(
    (data, type) => {
      select(data, type);
      handleSubMenuClose();
      handleCloseMenu();
    },
    [handleCloseMenu, handleSubMenuClose, select],
  );
  const handleMenuSelect = useCallback(
    type => {
      selectMenu(type);
    },
    [selectMenu],
  );
  return loading ? (
    <Box sx={{ ...menuPosition, position: 'fixed' }}>
      <CircularProgress color='secondary' size='20px' />
    </Box>
  ) : (
    <StyledContextPopover
      //keepMounted
      onClose={handleCloseMenu}
      open={openMenu}
      elevation={0}
      sx={{ ...menuPosition }}
      //anchorPosition={menuPosition}
      //anchorEl={anchor}
      anchorReference='none'
      slotProps={{ paper: { sx: { width: '180px' } } }}
    >
      <Box>
        <Grid container direction='column'>
          {menuItems.map((item, index) => (
            <Grid
              key={index}
              onClick={() => handleMenuSelect(item.name)}
              sx={{
                padding: item.subItems ? '6px 0 6px 20px' : '6px 20px',
                cursor: 'pointer',

                '&:hover': {
                  backgroundColor: '#E4E4EF',
                  '& svg path': {
                    fill: '#212346',
                  },
                },
              }}
            >
              <Grid
                container
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='h5'
                  sx={{
                    color: '#212346',
                  }}
                >
                  {getLocalString('labels', item.name)}
                </Typography>
                {item.subItems && (
                  <IconButton
                    disableRipple
                    sx={{ padding: 0 }}
                    onClick={event => handleSubMenu(event, item.name)}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                      }}
                    >
                      <ChevronIcon
                        direction='right'
                        color='rgba(228, 228, 239, 1)'
                        viewBox='0 0 24 24'
                      />
                    </Box>
                  </IconButton>
                )}
                {item.subItems &&
                  activeSubMenu &&
                  activeSubMenu === item.name && (
                    <StyledContextPopover
                      onClose={handleSubMenuClose}
                      keepMounted
                      open={activeSubMenu === item.name}
                      elevation={0}
                      anchorEl={anchorEl}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      slotProps={{
                        paper: { sx: { width: '180px', maxHeight: '200px' } },
                      }}
                    >
                      <StyledScrollContainer
                        sx={{
                          maxHeight: '200px',
                          overflow: 'auto',
                        }}
                      >
                        {item.subItems &&
                          item.subItems.map((subItem, index) => {
                            return (
                              <Grid
                                container
                                key={index}
                                onClick={() =>
                                  handleSubMenuSelect(subItem, item.name)
                                }
                                sx={{
                                  flexWrap: 'nowrap',
                                  padding: subItem.isChecked
                                    ? ' 6px 20px 6px 0'
                                    : '6px 20px',
                                  alignItems: 'center',
                                  cursor: 'pointer',

                                  '&:hover': {
                                    backgroundColor: '#E4E4EF',
                                  },
                                }}
                              >
                                {subItem.isChecked && (
                                  <Box
                                    component={'span'}
                                    sx={{
                                      width: '16px',
                                      height: '16px',
                                      marginRight: '2px',
                                    }}
                                  >
                                    <DoneIcon
                                      color='rgba(55, 198, 112, 1)'
                                      viewBox='0 0 24 24'
                                    />
                                  </Box>
                                )}
                                <Typography
                                  variant='h5'
                                  sx={{
                                    color: '#212346',
                                  }}
                                >
                                  {subItem.valueName}
                                </Typography>
                              </Grid>
                            );
                          })}
                      </StyledScrollContainer>
                    </StyledContextPopover>
                  )}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Box>
    </StyledContextPopover>
  );
};

export default ContextMenu;
